import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../utils";
import { Tooltip } from "../tooltip/Tooltip";

const buttonVariants = cva(
  "al-inline-flex al-items-center al-justify-center al-gap-2 al-whitespace-nowrap al-text-sm al-font-medium al-ring-offset-background al-transition-colors focus-visible:al-outline-none focus-visible:al-ring-2 focus-visible:al-ring-ring focus-visible:al-ring-offset-2 disabled:al-pointer-events-none disabled:al-opacity-50 [&_svg]:al-pointer-events-none [&_svg]:al-size-4 [&_svg]:al-shrink-0",
  {
    variants: {
      variant: {
        default: "al-bg-primary al-text-primary-foreground hover:al-bg-primary-hover",
        destructive:
          "al-bg-destructive al-text-destructive-foreground hover:al-bg-destructive-hover",
        outline:
          "al-border al-border-input al-bg-background hover:al-bg-accent hover:al-text-accent-foreground",
        success: "al-bg-success al-text-white hover:al-bg-success-hover",
        secondary:
          "al-bg-secondary al-text-secondary-foreground al-border al-border-blue-500 hover:al-text-white hover:al-bg-secondary-hover",
        ghost: "hover:al-bg-accent hover:al-text-primary",
        link: "al-text-primary al-underline-offset-4 hover:al-underline",
      },
      size: {
        default: "al-h-10 al-rounded-sm al-px-3 al-py-2",
        sm: "al-h-9 al-rounded-sm al-px-2 al-py-2",
        xs: "al-rounded-sm al-py-1.5 al-px-2 al-text-xs",
        lg: "al-h-11 al-rounded-md al-py-3 al-px-4",
        icon: "al-h-10 al-rounded-md al-w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

// Export size type explicitly to ensure proper .d.ts generation
export type ButtonSize = "default" | "sm" | "xs" | "lg" | "icon";

type BaseButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  Omit<VariantProps<typeof buttonVariants>, "size"> & {
    asChild?: boolean;
  };

// Generic props type with literal type inference
// Uses tuple trick [TSize] extends ["icon"] to prevent distributive conditional types
export type ButtonProps<TSize extends ButtonSize | undefined = undefined> = BaseButtonProps &
  ([TSize] extends ["icon"]
    ? { size: "icon"; title: string }
    : [TSize] extends [undefined]
      ? { size?: Exclude<ButtonSize, "icon">; title?: string }
      : { size?: TSize; title?: string });

// Internal implementation using the most permissive type
type ButtonPropsInternal = BaseButtonProps & {
  size?: ButtonSize;
  title?: string;
};

const ButtonComponent = (
  { className, variant, size, asChild = false, title, ...props }: ButtonPropsInternal,
  ref: React.Ref<HTMLButtonElement>
) => {
  const Comp = asChild ? Slot : "button";
  const button = (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
      aria-label={props["aria-label"] || title}
    />
  );

  // Wrap all buttons with Tooltip if title is provided
  if (title) {
    return <Tooltip title={title}>{button}</Tooltip>;
  }

  return button;
};

const ButtonWithRef = React.forwardRef(ButtonComponent);

// Cast to generic component with proper typing
type GenericButtonComponent = <TSize extends ButtonSize | undefined = undefined>(
  props: ButtonProps<TSize> & React.RefAttributes<HTMLButtonElement>
) => React.ReactElement | null;

const Button = ButtonWithRef as GenericButtonComponent & {
  displayName?: string;
};

Button.displayName = "Button";

export { Button, buttonVariants };
